import axios from 'axios';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { API } from '../CommonUtils/axiosWithDefaults';
import { httpBodyOptions } from '../CommonUtils/httpBodyOptions';
import { urlWithParams } from '../CommonUtils/urlWithParams';
import { ApiError } from '../errors/ApiError';
import { setPlanImpact } from '../FinancialPlan/FinancialPlanActions';
import {
  getMonteCarloQueryData,
  invalidateFinancialPlanQuery,
  invalidateLongevityAnalysis,
  invalidateMonteCarloQuery,
  monteCarloQueryDataRequest,
  updateFinancialPlanQueryData,
  invalidateMarketScenariosQuery,
  longevityDataRequest,
} from '../FinancialPlanOPL/Goals/helpers/legacyWhatIfsFinancialPlanHelpers';
import { stageWhatIfs } from '../MonteCarloChart/MonteCarloChartActions';
import { RETIREMENT_AGES_WHAT_IF_PATH } from './Retirement/General/RetirementAgesConstants';
import {
  selectPrincipalAge,
  selectSpouseAge,
} from './Retirement/SocialSecurity/MonthlyBenefitsSelector';
import { ASSET_ALLOCATION_WHAT_IFS } from './WhatIfsConstants';
import { getModelPortfolios, getModelPortfoliosGroups } from './whatIfsHelpers';
import { selectedSectionSelector } from '../FinancialPlan/Retirement/RetirementSelector';

let planImpactAbortController = null;

const action = name => `ADV.WHAT_IFS.${name}`;

export const SET_VISIBLE = action('SET_VISIBLE');
export const SET_OPEN = action('SET_OPEN');
export const SET_COMPARE_GOALS_OPEN = action('SET_COMPARE_GOALS_OPEN');
export const SET_DETAILED_ASSET_ALLOCATION_OPEN = action('SET_DETAILED_ASSET_ALLOCATION_OPEN');
export const SET_ERRORS = action('SET_ERRORS');
export const SET_CURRENT_VIEW_ID = action('SET_CURRENT_VIEW_ID');
export const SET_SELECTED_SECTION = action('SET_SELECTED_SECTION');
export const SET_WHAT_IFS_DATA = action('SET_WHAT_IFS_DATA');
export const INCREMENT_WHAT_IFS_SPINNER_LOADING = action('INCREMENT_WHAT_IFS_SPINNER_LOADING');
export const DECREMENT_WHAT_IFS_SPINNER_LOADING = action('DECREMENT_WHAT_IFS_SPINNER_LOADING');
export const INCREMENT_PLAN_IMPACT_SPINNER_LOADING = action(
  'INCREMENT_PLAN_IMPACT_SPINNER_LOADING',
);
export const DECREMENT_PLAN_IMPACT_SPINNER_LOADING = action(
  'DECREMENT_PLAN_IMPACT_SPINNER_LOADING ',
);
export const ACQUIRE_POPUP_SPINNER_LOADING = action('ACQUIRE_POPUP_SPINNER_LOADING ');
export const RELEASE_POPUP_SPINNER_LOADING = action('RELEASE_POPUP_SPINNER_LOADING ');
export const UPDATE_WHAT_IFS_DATA = action('UPDATE_WHAT_IFS_DATA');
export const UPDATE_COMPARE_GOALS_DATA = action('UPDATE_COMPARE_GOALS_DATA');
export const UPDATE_COMPARE_GOALS_PLAN_IMPACTS = action('UPDATE_COMPARE_GOALS_PLAN_IMPACTS');
export const OPTIMIZE_COMPARE_GOALS = action('OPTIMIZE_COMPARE_GOALS');

export const SET_MODEL_PORTFOLIO_GROUPS = action('SET_MODEL_PORTFOLIO_GROUPS');

export const OPL_MONTE_CARLO_ACTIVE = action('OPL_MONTE_CARLO_ACTIVE');

export const SET_WHAT_IFS_BODY = action('SET_WHAT_IFS_BODY');

export const setVisible = (clientId, visible) => ({
  type: SET_VISIBLE,
  clientId,
  visible,
});
export const setCompareGoalsOpen = (clientId, compareGoalsOpen) => ({
  type: SET_COMPARE_GOALS_OPEN,
  clientId,
  compareGoalsOpen,
});
export const setDetailedAssetAllocationOpen = (clientId, detailedAssetAllocationOpen) => ({
  type: SET_DETAILED_ASSET_ALLOCATION_OPEN,
  clientId,
  detailedAssetAllocationOpen,
});
export const setErrors = (clientId, errors) => ({
  type: SET_ERRORS,
  clientId,
  errors,
});
export const incrementWhatIfsSpinnerLoading = clientId => ({
  type: INCREMENT_WHAT_IFS_SPINNER_LOADING,
  clientId,
});

export const decrementWhatIfsSpinnerLoading = clientId => ({
  type: DECREMENT_WHAT_IFS_SPINNER_LOADING,
  clientId,
});

export const incrementPlanImpactSpinnerLoading = clientId => ({
  type: INCREMENT_PLAN_IMPACT_SPINNER_LOADING,
  clientId,
});

export const decrementPlanImpactSpinnerLoading = clientId => ({
  type: DECREMENT_PLAN_IMPACT_SPINNER_LOADING,
  clientId,
});

export const acquirePopupSpinnerLoading = clientId => ({
  type: ACQUIRE_POPUP_SPINNER_LOADING,
  clientId,
});

export const releasePopupSpinnerLoading = clientId => ({
  type: RELEASE_POPUP_SPINNER_LOADING,
  clientId,
});

export const setOpen = (clientId, open) => ({
  type: SET_OPEN,
  clientId,
  open,
});

export const setWhatIfsBody = body => ({
  type: SET_WHAT_IFS_BODY,
  payload: body,
});

export const setCurrentViewId = (clientId, goalId, currentViewId) => ({
  type: SET_CURRENT_VIEW_ID,
  clientId,
  goalId,
  currentViewId,
});

export const setSelectedSection = (clientId, goalId, selectedSection) => ({
  type: SET_SELECTED_SECTION,
  clientId,
  goalId,
  selectedSection,
});

export const updateWhatIfsData = (clientId, goalId, whatIfType, data) => ({
  type: UPDATE_WHAT_IFS_DATA,
  clientId,
  goalId,
  whatIfType,
  data,
});

export const updateCompareGoalsData = (path, value, clientId) => ({
  type: UPDATE_COMPARE_GOALS_DATA,
  clientId,
  path,
  value,
});

export const updateCompareGoalsPlanImpacts = (
  impacts,
  clientId,
  goalId,
  whatIfKey,
  whatIfType,
  availableCashflow,
  maxCashflow,
) => ({
  type: UPDATE_COMPARE_GOALS_PLAN_IMPACTS,
  clientId,
  impacts,
  goalId,
  whatIfKey,
  whatIfType,
  availableCashflow,
  maxCashflow,
});

export const optimizeCompareGoals = (whatIfs, clientId, goalId, whatIfType, whatIfKey) => ({
  type: OPTIMIZE_COMPARE_GOALS,
  clientId,
  whatIfs,
  goalId,
  whatIfType,
  whatIfKey,
});

export const setWhatIfsData = (clientId, goalId, data) => ({
  type: SET_WHAT_IFS_DATA,
  clientId,
  goalId,
  data,
});

export const acquireWhatIfsLoadingSpinner = (clientId, dispatch) => () =>
  dispatch(incrementWhatIfsSpinnerLoading(clientId));
export const releaseWhatIfsLoadingSpinner = (clientId, dispatch) => () =>
  dispatch(decrementWhatIfsSpinnerLoading(clientId));

export const acquirePlanImpactLoadingSpinner = (clientId, dispatch) => () =>
  dispatch(incrementPlanImpactSpinnerLoading(clientId));
export const releasePlanImpactLoadingSpinner = (clientId, dispatch) => () =>
  dispatch(decrementPlanImpactSpinnerLoading(clientId));

export const fetchWhatIfOptions = (
  clientId,
  fpId,
  goalId,
  whatIfPath,
  wiType,
  callback,
) => dispatch => {
  const { ADV } = window;
  acquireWhatIfsLoadingSpinner(clientId, dispatch)();
  ADV.util
    .request({
      uri: `Consumer/${whatIfPath}/Options`,
      data: {
        clientId,
        fpId,
        goalId,
      },
      showSpinner: false,
    })
    .then(response => {
      if (response.success) {
        dispatch(setErrors(clientId, []));

        if (ASSET_ALLOCATION_WHAT_IFS.indexOf(wiType) >= 0) {
          dispatch({
            type: SET_MODEL_PORTFOLIO_GROUPS,
            clientId,
            groups: getModelPortfoliosGroups(response.payload.viewData.ModelPortfolios),
          });

          dispatch(
            updateWhatIfsData(clientId, goalId, wiType, {
              Options: getModelPortfolios(response.payload.viewData.ModelPortfolios),
            }),
          );
        } else {
          dispatch(
            updateWhatIfsData(clientId, goalId, wiType, {
              Options: response.payload.viewData,
            }),
          );
        }
        if (callback) {
          callback(response.payload.viewData);
        }
      } else {
        dispatch(setErrors(clientId, response.errors));
      }
    })
    .catch(() => {
      dispatch(setErrors(clientId, [ADV.settings.MsgLib.generic_error]));
    })
    .then(
      releaseWhatIfsLoadingSpinner(clientId, dispatch),
      releaseWhatIfsLoadingSpinner(clientId, dispatch),
    );
};

export const applyWhatIf = (
  clientId,
  fpId,
  goalId,
  whatIf,
  whatIfPath,
  whatIfPickFields,
  whatIfType,
  callback,
) => (dispatch, getState) => {
  const monteCarloReduxCacheData = get(
    getState(),
    `MonteCarloChart.${clientId}.responseData.MonteCarloChartData`,
  );

  const principalAge = selectPrincipalAge(getState());
  const spouseAge = selectSpouseAge(getState());

  const FRAOption = {
    ...(principalAge === 'FRA'
      ? { UseFraPrincipal: true, SocialSecurityStartAgePrincipal: 0 }
      : { UseFraPrincipal: null }),
    ...(spouseAge === 'FRA'
      ? { UseFraSpouse: true, SocialSecurityStartAgeSpouse: 0 }
      : { UseFraSpouse: null }),
  };

  const requestData = {
    WhatIf: pick({ ...whatIf, ...FRAOption }, whatIfPickFields),
  };

  const monteCarloBaseData = {
    ConsumerId: clientId,
    PlanId: fpId,
    AdviserId: get(getState(), 'AdviserArea.adviser.id'),
  };

  if (monteCarloReduxCacheData) {
    requestData.UpdateMcCacheData = {
      ...monteCarloBaseData,
      CachedResults: monteCarloReduxCacheData,
    };
  }

  /**
   * If in OPL goal, use the OPL data
   */
  const isSummaryOfTrialsMounted = get(getState(), 'WhatIfs.isMonteCarloActive', false);
  const { MonteCarloChartData, isChanged } = getMonteCarloQueryData({ clientId, fpId });
  if (isSummaryOfTrialsMounted && isChanged) {
    requestData.UpdateMcCacheData = {
      ...monteCarloBaseData,
      CachedResults: MonteCarloChartData,
    };
  }

  const { ADV } = window;
  acquireWhatIfsLoadingSpinner(clientId, dispatch)();
  ADV.util
    .request({
      type: 'POST',
      uri: `Consumer/${whatIfPath}/ApplyToPlan`,
      urlParams: {
        clientId,
        fpId,
        goalId,
      },
      sendStringifyJSON: true,
      data:
        whatIfPath === RETIREMENT_AGES_WHAT_IF_PATH
          ? { ...requestData, WhatIf: { ...requestData.WhatIf, IsCreatedBySystem: false } }
          : requestData,
      showSpinner: false,
    })
    .then(response => {
      if (response.success) {
        // OPL Migration
        // ticket: 66757
        invalidateFinancialPlanQuery({ clientId, fpId });

        invalidateMarketScenariosQuery({ clientId, fpId });
        invalidateLongevityAnalysis({ clientId, fpId });
        /**
         * Note: do not invalidate the MC query here. We don't want to
         * refetch the MC data when applying the what-if to the plan.
         */

        //  End OPL Migration

        if (callback) {
          callback();
          return;
        }
        dispatch(setErrors(clientId, []));
        dispatch(updateWhatIfsData(clientId, goalId, whatIfType, response.payload.viewData));
        ADV.NewFinancialPlan.refreshPlan(true);
        dispatch(setCurrentViewId(clientId, goalId, null));
      } else {
        dispatch(setErrors(clientId, response.errors));
      }
    })
    .catch(() => {
      dispatch(setErrors(clientId, [ADV.settings.MsgLib.generic_error]));
    })
    .then(
      releaseWhatIfsLoadingSpinner(clientId, dispatch),
      releaseWhatIfsLoadingSpinner(clientId, dispatch),
    );
};

export const fetchGoalWhatIfData = (clientId, fpId, goalId, goalPath, debtType) => dispatch => {
  const { ADV } = window;
  acquireWhatIfsLoadingSpinner(clientId, dispatch)();
  ADV.util
    .request({
      uri: `Consumer/FinancialPlanWhatIfs/${goalPath}`,
      data: {
        clientId,
        fpId,
        goalId,
        debtType,
      },
      showSpinner: false,
    })
    .then(response => {
      if (response.success) {
        dispatch(setErrors(clientId, []));

        dispatch(setWhatIfsData(clientId, goalId, response.payload.viewData));
      } else {
        dispatch(setErrors(clientId, response.errors));
      }
    })
    .catch(() => {
      dispatch(setErrors(clientId, [ADV.settings.MsgLib.generic_error]));
    })
    .then(
      releaseWhatIfsLoadingSpinner(clientId, dispatch),
      releaseWhatIfsLoadingSpinner(clientId, dispatch),
    );
};

export const removeWhatIfs = (
  clientId,
  fpId,
  goalId,
  goalPath,
  WhatIfsKeys,
  debtType,
) => dispatch => {
  const { ADV } = window;
  acquireWhatIfsLoadingSpinner(clientId, dispatch)();
  ADV.util
    .request({
      type: 'POST',
      uri: 'Consumer/FinancialPlanWhatIfs/RemoveWhatIfs',
      urlParams: {
        clientId,
        fpId,
        goalId,
      },
      data: {
        WhatIfsKeys,
      },
      sendStringifyJSON: true,
      showSpinner: false,
    })
    .then(response => {
      dispatch(setErrors(clientId, []));
      dispatch(fetchGoalWhatIfData(clientId, fpId, goalId, goalPath, debtType));
      ADV.NewFinancialPlan.refreshPlan(true);

      if (response.success) {
        // OPL Migration
        // ticket: 66757
        invalidateFinancialPlanQuery({ clientId, fpId });

        invalidateMonteCarloQuery({ clientId, fpId });
        invalidateMarketScenariosQuery({ clientId, fpId });

        invalidateLongevityAnalysis({ clientId, fpId });
        //  End OPL Migration
      }
    })
    .catch(() => {
      dispatch(setErrors(clientId, [ADV.settings.MsgLib.generic_error]));
    })
    .then(
      releaseWhatIfsLoadingSpinner(clientId, dispatch),
      releaseWhatIfsLoadingSpinner(clientId, dispatch),
    );
};
// Note:
// If the Distribution What-If was already applied FE has no way to know the initial order of the distributions.
// Need to un-apply the what if, re-fetch options and apply it again.
// Another solution is to remove reset distribution functionality and use the default
// when the what if is un-applied.
export const removeFetchOptionsApply = (
  clientId,
  fpId,
  goalId,
  whatIfPath,
  whatIf,
  whatIfPickFields,
  callback,
) => dispatch => {
  const { ADV } = window;
  acquireWhatIfsLoadingSpinner(clientId, dispatch)();
  ADV.util
    .request({
      type: 'POST',
      uri: 'Consumer/FinancialPlanWhatIfs/RemoveWhatIfs',
      urlParams: {
        clientId,
        fpId,
        goalId,
      },
      data: {
        WhatIfsKeys: [whatIf.WhatIfKey],
      },
      sendStringifyJSON: true,
      showSpinner: false,
    })
    .then(response => {
      dispatch(setErrors(clientId, []));
      dispatch(
        fetchWhatIfOptions(clientId, fpId, goalId, whatIfPath, whatIf.WhatIfType, () => {
          dispatch(
            applyWhatIf(
              clientId,
              fpId,
              goalId,
              whatIf,
              whatIfPath,
              whatIfPickFields,
              whatIf.WhatIfType,
              callback,
            ),
          );
        }),
      );

      if (response.success) {
        // OPL Migration
        // ticket: 66757
        invalidateFinancialPlanQuery({ clientId, fpId });
      }
    })
    .catch(() => {
      dispatch(setErrors(clientId, [ADV.settings.MsgLib.generic_error]));
    })
    .then(
      releaseWhatIfsLoadingSpinner(clientId, dispatch),
      releaseWhatIfsLoadingSpinner(clientId, dispatch),
    );
};

const internalPlanImpact = (
  dispatch,
  clientId,
  fpId,
  goalId,
  whatIf,
  whatIfPath,
  whatIfPickFields,
  whatIfType,
  onStartLoading,
  onStopLoading,
  getState,
  isInitialPlanImpact,
) => {
  if (planImpactAbortController) {
    planImpactAbortController.abort();
  }
  planImpactAbortController = new AbortController();
  const { signal } = planImpactAbortController;

  const principalAge = selectPrincipalAge(getState());
  const spouseAge = selectSpouseAge(getState());

  const FRAOption = {
    ...(principalAge === 'FRA'
      ? { UseFraPrincipal: true, SocialSecurityStartAgePrincipal: 0 }
      : { UseFraPrincipal: null }),
    ...(spouseAge === 'FRA'
      ? { UseFraSpouse: true, SocialSecurityStartAgeSpouse: 0 }
      : { UseFraSpouse: null }),
  };

  const requestBody = pick({ ...whatIf, ...FRAOption }, whatIfPickFields);

  const url = urlWithParams(`/Consumer/${whatIfPath}/PlanImpact`, {
    clientId,
    fpId,
    goalId,
  });
  const { headers, body } = httpBodyOptions(requestBody, true);

  onStartLoading(clientId, dispatch).call();
  API.post(url, body, { signal, headers })
    .then(async viewData => {
      const isOPLMonteCarloMounted = get(getState(), 'WhatIfs.isMonteCarloActive', false);

      const whatIfsBody = Array.isArray(requestBody) ? requestBody : [requestBody];

      if (!isInitialPlanImpact) {
        dispatch(setWhatIfsBody(whatIfsBody));
      }
      if (isOPLMonteCarloMounted && !isInitialPlanImpact) {
        // This MC POST uses a dedicated abort key, so it is only aborted when a
        // newer plan-impact chain supersedes it. The AbortError then propagates to
        // the catch below so this stale chain stops here and the newest chain
        // performs the plan-impact updates.
        await monteCarloQueryDataRequest({ clientId, fpId, body: whatIfsBody });
      }

      const isLongevity = selectedSectionSelector(getState(), {}) === 'Longevity Analysis';
      if (isLongevity) {
        await longevityDataRequest({ clientId, fpId, body: whatIfsBody });
      }

      // OPL Migration
      // ticket: 66757
      const { financialPlan, availableCashflow, whatIfPlanImpact, maxCashflow } = viewData;

      updateFinancialPlanQueryData({
        clientId,
        fpId,
        financialPlan,
      });

      dispatch(setErrors(clientId, []));
      dispatch(
        updateWhatIfsData(clientId, goalId, whatIfType, {
          Impact: whatIfPlanImpact.map(impact => ({
            label: impact.Label,
            current: impact.Current,
            proposed: impact.Proposed,
          })),
          Options: {
            AvailableCashflow: availableCashflow,
            MaxCashflow: maxCashflow,
          },
        }),
      );
      dispatch(setPlanImpact(clientId, financialPlan));
    })
    .catch(err => {
      if (axios.isCancel(err)) {
        return;
      }
      // A superseded MC POST (fetch) rejects with a DOMException AbortError; treat
      // it like an axios cancel so this stale chain stops without showing an error.
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      if (err instanceof ApiError) {
        dispatch(setErrors(clientId, [err.message]));
        return;
      }
      dispatch(setErrors(clientId, [window.ADV.settings.MsgLib.generic_error]));
    })
    .then(onStopLoading(clientId, dispatch), onStopLoading(clientId, dispatch));
};

export const planImpact = (
  clientId,
  fpId,
  goalId,
  whatIf,
  whatIfPath,
  whatIfPickFields,
  whatIfType,
) => (dispatch, getState) => {
  dispatch(stageWhatIfs(clientId, [pick(whatIf, whatIfPickFields)]));
  internalPlanImpact(
    dispatch,
    clientId,
    fpId,
    goalId,
    whatIf,
    whatIfPath,
    whatIfPickFields,
    whatIfType,
    acquirePlanImpactLoadingSpinner,
    releasePlanImpactLoadingSpinner,
    getState,
    false,
  );
};

export const initialPlanImpact = (
  clientId,
  fpId,
  goalId,
  whatIf,
  whatIfPath,
  whatIfPickFields,
  whatIfType,
) => (dispatch, getState) => {
  internalPlanImpact(
    dispatch,
    clientId,
    fpId,
    goalId,
    whatIf,
    whatIfPath,
    whatIfPickFields,
    whatIfType,
    acquireWhatIfsLoadingSpinner,
    releaseWhatIfsLoadingSpinner,
    getState,
    true,
  );
};

export const resetModelPortfoliosGroups = clientId => ({
  type: SET_MODEL_PORTFOLIO_GROUPS,
  clientId,
  groups: [],
});
